
import React, { useState } from 'react';
import { BenposUploader } from './BenposUploader';
import { BenposBeneficiaries } from './BenposBeneficiaries';
import { BenposPayouts } from './BenposPayouts';
import { BenposVerification } from './BenposVerification';
import { sampleBenpos } from './data/sampleBenpos';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileTextIcon, 
  FileMinusIcon, 
  CalculatorIcon, 
  BanknoteIcon,
  CreditCardIcon
} from 'lucide-react';
import { toast } from "sonner";

export const BenposManagement = () => {
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [benposData, setBenposData] = useState(sampleBenpos);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tdsWaivers, setTdsWaivers] = useState<Record<string, boolean>>({});
  const [calculatedPayouts, setCalculatedPayouts] = useState(false);
  const [verifiedAccounts, setVerifiedAccounts] = useState<Record<string, boolean>>({});
  
  // Handle file upload
  const handleFileUpload = (file: File) => {
    setSelectedFile(file);
    setIsProcessingFile(true);
    
    // Simulate processing delay
    setTimeout(() => {
      setIsProcessingFile(false);
      setBenposData(sampleBenpos); // In a real app, this would be actual data from the file
      setActiveStep(1); // Move to beneficiaries step after upload
    }, 2000);
  };

  // Handle TDS waiver
  const handleTdsWaiver = (folioId: string, hasWaiver: boolean) => {
    setTdsWaivers(prev => ({
      ...prev,
      [folioId]: hasWaiver
    }));
  };

  // Calculate payouts
  const calculatePayouts = () => {
    setCalculatedPayouts(true);
    toast.success("Interest payments calculated successfully");
    setActiveStep(2); // Move to verification step
  };

  // Process payment for all verified accounts
  const processPayment = () => {
    toast.success("Payment processing initiated for all verified accounts");
    setActiveStep(3); // Move to payment confirmation
  };

  // Get step status
  const getStepStatus = (step: number) => {
    if (activeStep > step) return "completed";
    if (activeStep === step) return "active";
    return "pending";
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">NCD Interest Payments (BENPOS)</h3>
        <p className="text-sm text-muted-foreground">
          Manage interest payments to NCD holders using BENPOS (Beneficiary Position) data
        </p>
        
        {/* Workflow Steps */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <Card className={`flex-1 ${getStepStatus(0) === "active" ? "border-primary" : getStepStatus(0) === "completed" ? "border-green-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-full ${getStepStatus(0) === "active" ? "bg-primary text-primary-foreground" : 
                getStepStatus(0) === "completed" ? "bg-green-500 text-white" : "bg-muted"}`}>
                <FileTextIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">Step 1</p>
                <p className="text-sm text-muted-foreground">Upload BENPOS</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`flex-1 ${getStepStatus(1) === "active" ? "border-primary" : getStepStatus(1) === "completed" ? "border-green-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-full ${getStepStatus(1) === "active" ? "bg-primary text-primary-foreground" : 
                getStepStatus(1) === "completed" ? "bg-green-500 text-white" : "bg-muted"}`}>
                <FileMinusIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">Step 2</p>
                <p className="text-sm text-muted-foreground">Manage TDS Waivers</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`flex-1 ${getStepStatus(2) === "active" ? "border-primary" : getStepStatus(2) === "completed" ? "border-green-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-full ${getStepStatus(2) === "active" ? "bg-primary text-primary-foreground" : 
                getStepStatus(2) === "completed" ? "bg-green-500 text-white" : "bg-muted"}`}>
                <BanknoteIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">Step 3</p>
                <p className="text-sm text-muted-foreground">Verify Accounts</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`flex-1 ${getStepStatus(3) === "active" ? "border-primary" : getStepStatus(3) === "completed" ? "border-green-500" : ""}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-full ${getStepStatus(3) === "active" ? "bg-primary text-primary-foreground" : 
                getStepStatus(3) === "completed" ? "bg-green-500 text-white" : "bg-muted"}`}>
                <CreditCardIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium">Step 4</p>
                <p className="text-sm text-muted-foreground">Process Payment</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Step Content */}
        {activeStep === 0 && (
          <div className="space-y-4">
            <BenposUploader 
              isProcessingFile={isProcessingFile} 
              onFileUpload={handleFileUpload}
              selectedFile={selectedFile}
            />
          </div>
        )}
        
        {activeStep === 1 && benposData.length > 0 && (
          <div className="space-y-4">
            <BenposBeneficiaries 
              data={benposData} 
              tdsWaivers={tdsWaivers}
              onTdsWaiver={handleTdsWaiver}
            />
            <div className="flex justify-end">
              <Button onClick={calculatePayouts} className="mt-4">
                <CalculatorIcon className="mr-2 h-4 w-4" />
                Calculate Payouts
              </Button>
            </div>
          </div>
        )}
        
        {activeStep === 2 && benposData.length > 0 && (
          <div className="space-y-4">
            <BenposVerification 
              data={benposData} 
              tdsWaivers={tdsWaivers}
              onComplete={() => setActiveStep(3)}
            />
          </div>
        )}
        
        {activeStep === 3 && benposData.length > 0 && (
          <div className="space-y-4">
            <BenposPayouts 
              data={benposData} 
              tdsWaivers={tdsWaivers} 
            />
            <div className="flex justify-end">
              <Button onClick={() => setActiveStep(0)} variant="outline" className="mr-2">
                Start New Payout Cycle
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
